import '@angular/localize';

export const AI_SETUP_CONFIG = {
  texts: {
    pageTitle: $localize`:@@aiSetup.pageTitle:What do you want to manage With AI`,
    pageSubtitle: $localize`:@@aiSetup.pageSubtitle:Describe what you need or the data structure you want to manage. AI will help you create and update database & data models with proper fields, types, and relationships. When your data design is ready, you won't need any application other than Rapider AI to manage your data, create custom forms, and automate your workflows. You can also upload reference files like spreadsheets, documents, or diagrams to provide more context to the AI about your data management needs.`,

    // Form Elements
    promptPlaceholder: $localize`:@@aiSetup.promptPlaceholder:Describe the data you want to create or update (e.g., 'Create a User database with name, email, phone fields and relation to Profile')`,
    generateButton: $localize`:@@aiSetup.generateButton:Generate Data Models`,
    uploadButton: $localize`:@@aiSetup.uploadButton:Upload Reference Files`,

    // Loading State
    loadingTitle: $localize`:@@aiSetup.loadingTitle:Analyzing your requirements...`,
    loadingDescription: $localize`:@@aiSetup.loadingDescription:This process might take 2-3 minutes as the AI designs your complete data architecture. Please do not close this page.`,

    // Update & Split View
    updateTitle: $localize`:@@aiSetup.updateTitle:Update Data Models`,
    updateDescription: $localize`:@@aiSetup.updateDescription:Ask the AI to modify the generated data model structure.`,
    sendUpdateButton: $localize`:@@aiSetup.sendUpdateButton:Send Request`,
    saveUpdatesButton: $localize`:@@aiSetup.saveUpdatesButton:Save Updates`,
    discardUpdatesButton: $localize`:@@aiSetup.discardUpdatesButton:Discard Updates`,
    originalVersion: $localize`:@@aiSetup.originalVersion:Original Version`,
    updatedVersion: $localize`:@@aiSetup.updatedVersion:Updated Version`,

    // Confirmations
    discardTitle: $localize`:@@aiSetup.discardTitle:Discard Updates?`,
    discardMessage: $localize`:@@aiSetup.discardMessage:Are you sure you want to discard the AI's updates? This action cannot be undone.`,
  },
  notifications: {
    generateSuccess: $localize`:@@aiSetup.notify.generateSuccess:Data models generated successfully.`,
    generateError: $localize`:@@aiSetup.notify.generateError:Failed to generate data models. Please try again.`,
    updateSuccess: $localize`:@@aiSetup.notify.updateSuccess:Data models updated successfully.`,
    updateError: $localize`:@@aiSetup.notify.updateError:Failed to apply updates.`,
    savedSuccess: $localize`:@@aiSetup.notify.savedSuccess:Updates have been saved to the definition.`,
    discardedSuccess: $localize`:@@aiSetup.notify.discardedSuccess:Updates discarded.`,
  },
};

export interface AiSetupSuggestion {
  title: string;
  description: string;
}

/**
 * PURE FUNCTION: Returns a static list of helpful prompt suggestions to guide the user.
 * Each suggestion now includes a short title for scannability.
 * @returns {AiSetupSuggestion[]} Array of suggestion objects.
 */
export function getAiSetupSuggestions(): AiSetupSuggestion[] {
  return [
    {
      title: $localize`:@@aiSetup.title1:Expenses`,
      description: $localize`:@@aiSetup.sugg1:I want to manage my expenses with a complete expense management system that tracks submissions, categories, receipts, policy limits, approvals, reimbursements, and anomaly detection.`,
    },
    {
      title: $localize`:@@aiSetup.title2:Projects`,
      description: $localize`:@@aiSetup.sugg2:I want to manage my projects with a project management system that organizes projects, tasks, milestones, teams, timelines, priorities, dependencies, and progress tracking.`,
    },
    {
      title: $localize`:@@aiSetup.title3:Invoicing`,
      description: $localize`:@@aiSetup.sugg3:I want to manage my invoices with an invoicing system that handles customers, invoice creation, line items, taxes, payment status, reminders, and collections.`,
    },
    {
      title: $localize`:@@aiSetup.title4:Daily Standups`,
      description: $localize`:@@aiSetup.sugg4:I want to manage daily status updates from my team with a system that collects updates, blockers, priorities, attendance, and weekly summaries by employee and team.`,
    },
    {
      title: $localize`:@@aiSetup.title5:Scheduling`,
      description: $localize`:@@aiSetup.sugg5:I want to manage teacher and parent scheduling with a system that handles appointments, availability, meeting requests, reminders, and communication history.`,
    },
    {
      title: $localize`:@@aiSetup.title6:Inventory`,
      description: $localize`:@@aiSetup.sugg6:I want to manage assets and inventory with a system that tracks assets, stock levels, locations, assignments, maintenance schedules, reorders, and inventory movements.`,
    },
    {
      title: $localize`:@@aiSetup.title7:Events`,
      description: $localize`:@@aiSetup.sugg7:I want to manage events with a system that handles event planning, attendees, vendors, budgets, schedules, venues, tasks, and post-event follow-up.`,
    },
    {
      title: $localize`:@@aiSetup.title8:Travel Agency`,
      description: $localize`:@@aiSetup.sugg8:I want to manage my travel agency with a system that organizes customers, bookings, itineraries, suppliers, payments, documents, and trip communications.`,
    },
    {
      title: $localize`:@@aiSetup.title9:Health Tracking`,
      description: $localize`:@@aiSetup.sugg9:I want to manage my daily health activities with a system that records meals, calories, weight, exercise, water intake, body measurements, and daily progress.`,
    },
    {
      title: $localize`:@@aiSetup.title10:Orders`,
      description: $localize`:@@aiSetup.sugg10:I want to manage my orders with an order management system that tracks customers, products, order details, addresses, payments, fulfillment, and delivery updates.`,
    },
    {
      title: $localize`:@@aiSetup.title11:Pet Adoption`,
      description: $localize`:@@aiSetup.sugg11:I want to manage pet adoption and veterinary intake with a system that handles adopters, pets, applications, medical records, fees, agreements, and intake workflows.`,
    },
    {
      title: $localize`:@@aiSetup.title12:Vendor Management`,
      description: $localize`:@@aiSetup.sugg12:I want to manage vendors with a vendor management system that handles onboarding, contacts, contracts, compliance documents, performance reviews, and renewals.`,
    },
    {
      title: $localize`:@@aiSetup.title13:IT Support`,
      description: $localize`:@@aiSetup.sugg13:I want to manage IT support requests with a service desk system that tracks tickets, categories, priorities, SLAs, assignees, resolutions, and user communication.`,
    },
    {
      title: $localize`:@@aiSetup.title14:Appointments`,
      description: $localize`:@@aiSetup.sugg14:I want to manage appointments and resource scheduling with a system that books services, rooms, equipment, staff availability, time slots, and confirmations.`,
    },
    {
      title: $localize`:@@aiSetup.title15:Loan Applications`,
      description: $localize`:@@aiSetup.sugg15:I want to manage loan or grant applications with a system that collects applicants, documents, eligibility checks, reviews, decisions, disbursements, and follow-ups.`,
    },
    {
      title: $localize`:@@aiSetup.title16:Audits`,
      description: $localize`:@@aiSetup.sugg16:I want to manage audits and inspections with a system that records checklists, findings, photos, corrective actions, inspectors, deadlines, and compliance status.`,
    },
    {
      title: $localize`:@@aiSetup.title17:Human Resources`,
      description: $localize`:@@aiSetup.sugg17:I want to manage my employees with an HR system that tracks employee profiles, departments, roles, documents, leave requests, onboarding, and performance notes.`,
    },
    {
      title: $localize`:@@aiSetup.title18:Recruitment`,
      description: $localize`:@@aiSetup.sugg18:I want to manage recruitment with a hiring system that tracks job postings, candidates, resumes, interview stages, feedback, and hiring decisions.`,
    },
    {
      title: $localize`:@@aiSetup.title19:Customer Support`,
      description: $localize`:@@aiSetup.sugg19:I want to manage customer support with a help desk system that handles customer profiles, cases, messages, priorities, agents, resolutions, and satisfaction tracking.`,
    },
    {
      title: $localize`:@@aiSetup.title20:CRM & Sales`,
      description: $localize`:@@aiSetup.sugg20:I want to manage sales leads with a CRM system that tracks leads, companies, contacts, pipeline stages, activities, follow-ups, and deal progress.`,
    },
    {
      title: $localize`:@@aiSetup.title21:Subscriptions`,
      description: $localize`:@@aiSetup.sugg21:I want to manage subscriptions with a system that handles plans, customers, billing cycles, renewals, upgrades, cancellations, and payment history.`,
    },
    {
      title: $localize`:@@aiSetup.title22:Contracts`,
      description: $localize`:@@aiSetup.sugg22:I want to manage contracts with a system that stores agreements, parties, terms, approvals, signatures, renewal dates, and obligation tracking.`,
    },
    {
      title: $localize`:@@aiSetup.title23:Procurement`,
      description: $localize`:@@aiSetup.sugg23:I want to manage procurement with a system that tracks purchase requests, approvals, vendors, quotations, purchase orders, receipts, and spending.`,
    },
    {
      title: $localize`:@@aiSetup.title24:Internal Approvals`,
      description: $localize`:@@aiSetup.sugg24:I want to manage internal requests and approvals with a system for leave, travel, procurement, and other requests with workflows, approvers, statuses, and audit history.`,
    },
    {
      title: $localize`:@@aiSetup.title25:Personal Tasks`,
      description: $localize`:@@aiSetup.sugg25:I want to manage tasks and personal productivity with a system that tracks tasks, goals, reminders, priorities, deadlines, routines, and completion history.`,
    },
    {
      title: $localize`:@@aiSetup.title26:Maintenance`,
      description: $localize`:@@aiSetup.sugg26:I want to manage maintenance operations with a system that schedules work orders, tracks equipment, technicians, spare parts, inspections, and service history.`,
    },
    {
      title: $localize`:@@aiSetup.title27:Real Estate`,
      description: $localize`:@@aiSetup.sugg27:I want to manage real estate listings and clients with a system that tracks properties, owners, buyers, inquiries, visits, offers, and contracts.`,
    },
    {
      title: $localize`:@@aiSetup.title28:School Operations`,
      description: $localize`:@@aiSetup.sugg28:I want to manage school operations with a system that organizes students, classes, teachers, schedules, attendance, grades, and parent communication.`,
    },
    {
      title: $localize`:@@aiSetup.title29:Clinic Appointments`,
      description: $localize`:@@aiSetup.sugg29:I want to manage clinic or medical office appointments with a system that tracks patients, providers, bookings, visit notes, billing, and follow-up reminders.`,
    },
    {
      title: $localize`:@@aiSetup.title30:Restaurants`,
      description: $localize`:@@aiSetup.sugg30:I want to manage restaurant operations with a system that handles tables, reservations, menu items, orders, inventory, staff shifts, and daily sales.`,
    },
    {
      title: $localize`:@@aiSetup.title31:Warehouses`,
      description: $localize`:@@aiSetup.sugg31:I want to manage warehouse operations with a system that tracks stock, bins, inbound shipments, outbound shipments, pick lists, and inventory accuracy.`,
    },
    {
      title: $localize`:@@aiSetup.title32:Legal Cases`,
      description: $localize`:@@aiSetup.sugg32:I want to manage legal cases with a system that organizes clients, matters, documents, deadlines, hearings, billing, and case notes.`,
    },
    {
      title: $localize`:@@aiSetup.title33:Insurance Claims`,
      description: $localize`:@@aiSetup.sugg33:I want to manage insurance claims with a system that tracks policyholders, claims, incidents, documents, assessments, approvals, and payouts.`,
    },
    {
      title: $localize`:@@aiSetup.title34:Memberships`,
      description: $localize`:@@aiSetup.sugg34:I want to manage memberships with a system that handles members, plans, renewals, check-ins, payments, and communication history.`,
    },
    {
      title: $localize`:@@aiSetup.title35:Content Production`,
      description: $localize`:@@aiSetup.sugg35:I want to manage content production with a system that organizes content ideas, drafts, approvals, publishing schedules, channels, and performance tracking.`,
    },
    {
      title: $localize`:@@aiSetup.title36:Manufacturing`,
      description: $localize`:@@aiSetup.sugg36:I want to manage manufacturing operations with a system that tracks products, bills of materials, production orders, work centers, quality checks, and output.`,
    },
  ];
}

/**
 * PURE FUNCTION: Splits an array into smaller chunk arrays of a specified size.
 * Allows easy rendering of grid-paged items inside a carousel.
 * @param {T[]} array - The array to chunk.
 * @param {number} size - The maximum size of each inner array.
 * @returns {T[][]} A two-dimensional array holding the chunks.
 */
export function chunkArray<T>(array: T[], size: number): T[][] {
  if (!array || size <= 0) return [];
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

/**
 * PURE FUNCTION: Builds the payload required by the generateDataModelTemplateLibrary SDK method.
 * Constructs the object matching the form-data parameter structure.
 * @param {string} message - The user's prompt instruction.
 * @param {File[]} files - Uploaded reference files.
 * @param {string} [definitionId] - Optional existing definition ID to contextually update an existing architecture.
 * @returns {any} The formatted payload object for the SDK.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildGenerateLibraryPayload(
  message: string,
  files: File[],
  definitionId?: string,
): any {
  return {
    body: {
      message: message,
      ...(definitionId ? { definitionId } : {}),
      files: files && files.length > 0 ? files : undefined,
    },
  };
}

/**
 * PURE FUNCTION: Builds the payload required to save (patch) the accepted AI updates back to the definition.
 * @param {string} definitionId - The ID of the original definition to update.
 * @param {any} updatedDefinition - The newly generated definition object.
 * @returns {any} The formatted payload object for the update SDK.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildSaveUpdatesPayload(
  definitionId: string,
  updatedDefinition: any,
): any {
  return {
    id: definitionId,
    body: updatedDefinition,
  };
}
